/* eslint-disable camelcase */
import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as yup from "yup"
import moment from "moment"
import { Appearance, TextInput, ViewStyle } from "react-native"
import { Cycle, Farm, Stock } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { RadioField } from "../../field/radio-field/radio-field"
import { CycleProFeature } from "../../cycle-pro-feature/cycle-pro-feature"
import { useStores } from "../../../models"
import _ from "lodash"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10,
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
}

export interface FeedFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    cycle_id?: number
    logged_at?: string
    logged_date?: string
    logged_time?: string
    stock_id?: number
    quantity?: number
    fasting?: boolean
    remark?: string
  }
}

/**
 * Describe your component here
 */
export const FeedForm = observer(function FeedForm(props: FeedFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const {
    appStore: { farmStore }
  } = useStores();

  const [ cycle, setCycle ] = useState<any>(null)

  useEffect(() => {
    const cycle = farmStore.cycles.find((c) => c.id === initialValuesProps.cycle_id)
    setCycle(cycle)
  }, [farmStore.cycles, initialValuesProps.cycle_id])

  const { pro } = cycle || {}

  const input = {
    quantity: useRef<TextInput>(),
    remark: useRef<TextInput>(),
  }

  const nextInput = {
    quantity: () => input.remark.current && input.remark.current.focus(),
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("feedForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("feedForm.cycleId")),
    logged_date: yup.mixed().nullable().required().label(translate("feedForm.loggedDate")),
    logged_time: yup.mixed().nullable().required().label(translate("feedForm.loggedTime")),
    stock_id: yup.number().nullable().label(translate('feedForm.stockId')),
    quantity: yup.number().max(1000).nullable().required().when('fasting', {
      is: true,
      then: (schema) => schema.min(0),
      otherwise: (schema) => schema.moreThan(0),
    }).label(translate("feedForm.quantity")),
    remark: yup.string().ascii().max(255).nullable().label(translate("feedForm.remark")),
  })

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      logged_at: values.logged_date
        && values.logged_time
        && moment(`${values.logged_date} ${values.logged_time}`).utc().format("YYYY-MM-DD HH:mm:ss")
        || null,
      get logged_date() {
        return this.logged_at && moment.utc(this.logged_at).format("YYYY-MM-DD")
      },
      get logged_time() {
        return this.logged_at && moment.utc(this.logged_at).format("HH:mm:ss")
      },
    })
  }, [onSubmitProps])

  const initialValues = useMemo<FeedFormProps["initialValues"]>(() => ({
    farm_id: null,
    ...initialValuesProps,
    logged_at: initialValuesProps.logged_at || moment.utc().startOf("minute").format("YYYY-MM-DD HH:mm:ss"),
    get logged_date() {
      return this.logged_at && moment.utc(this.logged_at).local().format("YYYY-MM-DD")
    },
    get logged_time() {
      return this.logged_at && moment.utc(this.logged_at).local().format("HH:mm:ss")
    },
    quantity: initialValuesProps.quantity || null,
    fasting: initialValuesProps.fasting != null ? initialValuesProps.fasting : null,
    remark: initialValuesProps.remark || null,
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="feedForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue("stock_id", null)
                await setFieldValue("cycle_id", null)
                await setFieldValue("farm_id", value || null)
                await setFieldTouched("farm_id")
              },
            }}
          />
          <WalkthroughElement
            id="feed-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={125}
          >
            <View style={TITLE}>
              <Text tx="feedForm.feeding" preset="title3" colorScheme={colorScheme} />
            </View>
            <View style={BORDER} />
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="feedForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  Promise.resolve(_.orderBy(
                    farmStore.cycles.map((cycle) => Cycle.hydrate(cycle)), ['started_at', 'pond_name_alpha_numeric'], ['desc', 'asc']
                  )).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  await setCycle(item && item.toArray() || null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "feedForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="logged_date"
              txPrefix="feedForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <View flexDirection="row">
              <DateTimePickerField
                name="logged_time"
                txPrefix="feedForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1, isRequired: true }}
                dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
              />
              <View
                flex={1}
                style={FORM_DROPDOWN}
              ></View>
            </View>
            <RadioField
              name="fasting"
              txPrefix="feedForm"
              colorScheme={colorScheme}
              radioGroupProps={{
                defaultValue: JSON.stringify(initialValues.fasting),
              }}
              radios={[{
                value: JSON.stringify(true),
                children: <Text tx="Yes" preset="body2" ml="6px" colorScheme={colorScheme} />
              }, {
                value: JSON.stringify(false),
                children: <Text tx="No" preset="body2" ml="6px" colorScheme={colorScheme} />
              }]}
              castValue={JSON.parse}
            />
            <CycleProFeature
              allow={pro}
              colorScheme={colorScheme}
            >
              <ModelField
                key={`${values.farm_id}-stock_id`}
                name="stock_id"
                txPrefix="feedForm"
                colorScheme={colorScheme}
                modelPickerProps={{
                  Model: Stock,
                  dataLabel: "product_name",
                  fetchData: (model, search) => (
                    model.fetch({
                      farm_id__eq: values.farm_id,
                      product_category_id__eq: 123,
                      search,
                      sort: 'product_name'
                    })
                  ),
                  renderSelectedItemText: (item) => item && `${item.product_name} ${item.product_unit && `(${item.product_unit})` || ''}`,
                  clearable: true,
                  isDisabled: !values.farm_id || !pro
                }}
              />
            </CycleProFeature>
            <NumeralField
              name="quantity"
              txPrefix="feedForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
            />
            <TextField
              name="remark"
              txPrefix="feedForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

FeedForm.displayName = 'FeedForm'
